<template>
    <div>
        <TableComponent
            v-bind="tableProps"
            v-model="selectedDevice"
            @on-refresh="fetchData"
            @on-change-page="changePage"
            @on-change-page-size="changePageSize"
            @on-filter="filterData"
            @on-text-filter="textFilterData"
            @on-sort="sortData"
        >
            <template v-slot:table-actions>
                <li class="nav-item">
                    <a class="nav-link" href="#" @click="showAddDeviceModal">
                        <i class="fa fa-plus-square"></i>
                        <p>{{ $t('devices.devices_msg__add') }}</p>
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" href="#" @click="deleteDevice">
                        <i class="fa fa-trash"></i>
                        <p>{{ $t('devices.devices_msg__remove') }}</p>
                    </a>
                </li>
            </template>
        </TableComponent>

        <!-- Add Device Modal -->
        <ModalComponent
            v-if="isAddModalVisible"
            id="device-add-modal"
            :visible="isAddModalVisible"
            :title="$t('devices.devices_msg__add') || 'Add device'"
            @close="isAddModalVisible = false"
        >
            <div class="container-fluid" style="white-space: pre-line">
                <div
                    v-if="!selectedAddMethod"
                    class="btn-group-vertical w-100 mb-3"
                    role="group"
                    aria-label="add-methods"
                >
                    <button
                        class="btn btn-primary w-100 mb-2"
                        :disabled="modalLoading"
                        @click="handleSelectAddMethod('manually')"
                    >
                        {{
                            $t(
                                'devices.devices_msg__manually_enter_the_registration_code'
                            )
                        }}
                    </button>
                    <button
                        class="btn btn-secondary w-100 mb-2"
                        :disabled="modalLoading"
                        @click="handleSelectAddMethod('qrcode')"
                    >
                        {{ $t('devices.devices_msg__scan_qr_code') }}
                    </button>
                    <button
                        class="btn btn-info w-100"
                        :disabled="modalLoading"
                        @click="handleSelectAddMethod('barcode')"
                    >
                        {{ $t('devices.devices_msg__scan_barcode') }}
                    </button>
                </div>

                <div v-if="modalLoading" class="my-3">
                    <i class="fa fa-spinner fa-spin"></i>
                </div>

                <!-- Manually: show 6-digit code -->
                <div v-if="!modalLoading && selectedAddMethod === 'manually'">
                    <p class="mb-1">
                        {{
                            $t(
                                'devices.devices_msg__manually_registration_code_text',
                                { expire_minutes }
                            )
                        }}
                    </p>
                    <h2 class="display-6">{{ registrationCode || '-' }}</h2>
                </div>

                <!-- Barcode: render barcode from 6-digit code -->
                <div v-if="!modalLoading && selectedAddMethod === 'barcode'">
                    <p class="mb-1">
                        {{
                            $t('devices.devices_msg__barcode_text', {
                                expire_minutes,
                            })
                        }}
                    </p>
                    <h4>{{ registrationCode || '-' }}</h4>
                    <div v-if="registrationCode">
                        <vue-barcode
                            :value="registrationCode"
                            tag="svg"
                            :options="{ format: 'CODE39', displayValue: false }"
                        />
                    </div>
                </div>

                <!-- QR Code -->
                <div v-if="!modalLoading && selectedAddMethod === 'qrcode'">
                    <p class="mb-1">
                        {{
                            $t('devices.devices_msg__qr_code_text', {
                                expire_minutes,
                            })
                        }}
                    </p>
                    <div v-if="qrText">
                        <qrcode-vue :value="qrText" :size="240" level="H" />
                    </div>
                    <div v-else-if="qrBase64">
                        <img
                            :src="qrBase64"
                            alt="QR Code"
                            style="max-width: 240px"
                        />
                    </div>
                    <div v-else>
                        <small>No QR yet</small>
                    </div>
                </div>
            </div>
        </ModalComponent>
    </div>
</template>

<script>
import { ref, onMounted, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import TableComponent from '@/components/TableComponent.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import QrcodeVue from 'qrcode.vue';
import VueBarcode from '@chenfengyuan/vue-barcode';

import {
    fetchDevicesFromAPI,
    fetchConfig as fetchDeviceConfig,
    deleteDevice as deleteDeviceFromAPI,
    getDevice,
    generateRegistrationCode,
    generateQRCode,
} from '@/api/iot-devices';

export default {
    components: {
        TableComponent,
        ModalComponent,
        QrcodeVue,
        VueBarcode,
    },
    setup() {
        const { t } = useI18n();
        const Swal = inject('$swal');
        const route = useRoute();
        const router = useRouter();

        const columnOrder = ref([]);
        const headers = ref([]);
        const data = ref([]);
        const pageSize = ref(10);
        const currentPage = ref(1);
        const totalPages = ref(1);
        const totalCounts = ref(0);
        const filter = ref('');
        const filterFields = ref([]);
        const searchFields = ref([]);
        const sortColumn = ref('');
        const sortOrder = ref('desc');
        const loading = ref(false);
        const params = ref({});

        const isAddModalVisible = ref(false);
        const selectedDevice = ref([]);

        // Add-device modal states
        const selectedAddMethod = ref('');
        const registrationCode = ref('');
        const qrBase64 = ref('');
        const qrText = ref(''); // NEW: text content for qrcode-vue
        const modalLoading = ref(false);
        const expire_minutes = ref(20); // default, will be updated

        const buildParams = () => {
            const p = {
                page: currentPage.value,
                page_size: pageSize.value,
                search: filter.value,
            };
            if (sortColumn.value) {
                p.ordering = `${sortOrder.value === 'desc' ? '-' : ''}${
                    sortColumn.value
                }`;
            }
            return p;
        };

        const loadConfig = async () => {
            const response = await fetchDeviceConfig();
            const sortableColumns = response.ordering_fields || [];
            columnOrder.value = response.display_fields || [];
            headers.value = columnOrder.value.map(key => ({
                text: t(`devices.devices_fld__${key}`) || t(key),
                value: key,
                sortable: sortableColumns.includes(key),
            }));
            const responseFilterFields = response.filter_fields || {};
            filterFields.value = Object.keys(responseFilterFields).map(key => ({
                key,
                label: t(`devices.devices_fld__${key}`) || key,
                type: responseFilterFields[key].type,
                value: '',
                start: '',
                end: '',
                options: responseFilterFields[key].items || [],
            }));
            if (response.search_fields) {
                searchFields.value = response.search_fields.map(key => ({
                    key,
                    label: t(`devices.devices_fld__${key}`) || key,
                }));
            }
        };

        const fetchData = async () => {
            isAddModalVisible.value = false;
            loading.value = true;
            try {
                const response = await fetchDevicesFromAPI(params.value);
                data.value = response.results;
                currentPage.value = response.current_page;
                totalCounts.value = response.total_count;
                totalPages.value = response.total_pages;
            } catch (error) {
                console.error(error);
            } finally {
                loading.value = false;
            }
        };

        const changePage = async page => {
            if (page >= 1 && page <= totalPages.value) {
                params.value.page = page;
                await fetchData();
            }
        };

        const changePageSize = async size => {
            params.value.page_size = size;
            await fetchData();
        };

        const filterData = async filter => {
            if (filter.type === 'datetime') {
                params.value[`${filter.key}__gte`] = filter.start;
                params.value[`${filter.key}__lte`] = filter.end;
            } else {
                params.value[filter.key] = filter.value;
            }
            params.value.page = 1;
            await fetchData();
        };

        const sortData = async (columnIndex, order) => {
            sortColumn.value = columnIndex;
            sortOrder.value = order;

            router.replace({
                path: route.path,
                query: {
                    ...route.query,
                    sortColumn: sortColumn.value,
                    sortOrder: sortOrder.value,
                },
            });

            params.value.ordering = `${sortOrder.value === 'desc' ? '-' : ''}${
                sortColumn.value
            }`;
            params.value.page = 1;
            await fetchData();
        };

        const textFilterData = async search => {
            if (search.field !== '') {
                params.value.search_fields = search.field;
            } else {
                params.value.search_fields = searchFields.value
                    .map(item => item.key)
                    .join(',');
            }
            params.value.search = search.text;
            params.value.page = 1;
            sortColumn.value = '';
            sortOrder.value = 'desc';
            delete params.value.ordering;
            await fetchData();
        };

        // Show Add Device modal and reset state
        const showAddDeviceModal = () => {
            selectedAddMethod.value = '';
            registrationCode.value = '';
            qrBase64.value = '';
            qrText.value = ''; // reset
            modalLoading.value = false;
            isAddModalVisible.value = true;
        };

        // Handle selection and call APIs
        const handleSelectAddMethod = async method => {
            selectedAddMethod.value = method;
            modalLoading.value = true;
            try {
                if (method === 'manually' || method === 'barcode') {
                    const resp = await generateRegistrationCode();
                    registrationCode.value = resp?.code || '';
                    expire_minutes.value = resp?.expire_minutes || 20;
                } else if (method === 'qrcode') {
                    const resp = await generateQRCode();

                    // Accept common response shapes:
                    // 1) plain string to encode => use qrcode-vue
                    // 2) object with .data/.qr/.value etc.
                    // 3) base64 image or Blob => fallback to <img>
                    const pick = resp?.data ?? resp?.qr ?? resp?.value ?? resp;
                    expire_minutes.value = resp?.expire_minutes || 20;

                    if (typeof pick === 'string') {
                        if (pick.startsWith('data:')) {
                            // it's already a data URL image
                            qrBase64.value = pick;
                            qrText.value = '';
                        } else {
                            // it's the content; feed to qrcode-vue
                            qrText.value = pick;
                            qrBase64.value = '';
                        }
                    } else if (pick instanceof Blob) {
                        qrBase64.value = await blobToDataURL(pick);
                        qrText.value = '';
                    } else {
                        // last resort: try to convert whole resp to data URL
                        qrBase64.value = await toDataUrl(resp);
                        qrText.value = '';
                    }
                }
            } catch (error) {
                console.error(error);
                Swal?.fire({
                    title:
                        t('devices.devices_msg__add_device_failed') ||
                        'Operation failed',
                    text: error?.response?.data?.message || '',
                    icon: 'error',
                });
            } finally {
                modalLoading.value = false;
            }
        };

        const showAlert = (titleKey, textKey, iconType, options = {}) => {
            Swal.fire({
                title: t(titleKey, options),
                text: textKey ? t(textKey, options) : '',
                icon: iconType,
            });
        };

        const handleApiCall = async (
            apiFunction,
            errorTitle,
            errorMessageKey
        ) => {
            try {
                return await apiFunction();
            } catch (error) {
                console.error(error);
                let errorMessage =
                    error.response?.status === 404
                        ? t('devices.devices_msg__not_found')
                        : t(`devices.${error.response?.data?.error}`);
                if (errorMessageKey && !errorMessage) {
                    errorMessage = t(`${errorMessageKey}`);
                }
                showAlert(errorTitle, errorMessage, 'error');
                return null;
            }
        };

        const deleteDevice = async () => {
            if (selectedDevice.value.length !== 1) {
                showAlert(
                    'devices.devices_msg__please_select_only_one_device_to_delete',
                    '',
                    'warning'
                );
                return;
            }

            const device = await handleApiCall(
                () => getDevice(selectedDevice.value[0]),
                'devices.devices_msg__delete_device_failed',
                'devices.devices_msg__not_found'
            );
            if (!device) {
                await fetchData();
                return;
            }

            const { value: confirmDelete } = await Swal.fire({
                title: t('devices.devices_msg__delete_device'),
                text: t('devices.devices_msg__delete_device_confirm', {
                    device_psn: device.psn,
                }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('devices.devices_msg__delete'),
                cancelButtonText: t('devices.devices_msg__cancel'),
            });

            if (!confirmDelete) return;

            const response = await handleApiCall(
                () => deleteDeviceFromAPI(selectedDevice.value[0]),
                'devices.devices_msg__delete_device_failed',
                'devices.devices_msg__delete_device_error'
            );

            if (response && response.status === 204) {
                showAlert(
                    'devices.devices_msg__delete_device_successful',
                    '',
                    'success',
                    { device_psn: device.psn }
                );
            }

            await fetchData();
        };

        const tableProps = computed(() => ({
            headers: headers.value,
            data: data.value,
            currentPage: currentPage.value,
            currentPageSize: params.value.page_size,
            totalPages: totalPages.value,
            loading: loading.value,
            sortColumn: sortColumn.value,
            sortOrder: sortOrder.value,
            filterOptions: filterFields.value,
            totalCounts: totalCounts.value,
            messageRefresh: t('devices.devices_msg__refresh'),
            messagePrevious: t('devices.devices_msg__previous'),
            messageNext: t('devices.devices_msg__next'),
            messageExport: t('devices.devices_msg__export'),
            messagePageSize: t('devices.devices_msg__page_size'),
            messageSearch: t('devices.devices_msg__search_placeholder'),
            messageSummary: 'devices.devices_msg__table_summary',
            pageSizes: [10, 20, 50, 100],
            searchFields: searchFields.value,
            selectable: true,
            selectionMode: 'single',
            selectionValueKey: 'psn',
            showExportButton: false,
        }));

        onMounted(async () => {
            const sortColumnFromQuery = route.query.sortColumn || '';
            const sortOrderFromQuery = route.query.sortOrder || '';

            sortColumn.value = sortColumnFromQuery;
            sortOrder.value = sortOrderFromQuery;

            await loadConfig();

            params.value = buildParams();

            if (sortColumn.value !== '' && sortOrder.value !== '') {
                params.value.ordering = `${
                    sortOrder.value === 'desc' ? '-' : ''
                }${sortColumn.value}`;
            }

            await fetchData();
        });

        // Helpers to normalize QR response
        const blobToDataURL = blob =>
            new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });

        const toDataUrl = async payload => {
            if (!payload) return '';
            // If API returns Blob directly
            if (payload instanceof Blob) return await blobToDataURL(payload);
            // If API returns string (with or without prefix)
            if (typeof payload === 'string') {
                return payload.startsWith('data:')
                    ? payload
                    : `data:image/png;base64,${payload}`;
            }
            // If API returns object, try common keys
            const val =
                payload.data ??
                payload.base64 ??
                payload.image ??
                payload.qr ??
                '';
            if (!val) return '';
            if (val instanceof Blob) return await blobToDataURL(val);
            if (typeof val === 'string') {
                return val.startsWith('data:')
                    ? val
                    : `data:image/png;base64,${val}`;
            }
            return '';
        };

        return {
            tableProps,
            fetchData,
            changePage,
            changePageSize,
            filterData,
            sortData,
            textFilterData,
            selectedDevice,
            deleteDevice,
            isAddModalVisible,
            showAddDeviceModal,
            handleSelectAddMethod,
            selectedAddMethod,
            registrationCode,
            qrBase64,
            qrText,
            modalLoading,
            expire_minutes,
        };
    },
};
</script>
